import { CommonModule } from '@angular/common';
import { Component, ElementRef, EventEmitter, Input, Output, SimpleChanges, ViewChild } from '@angular/core';
import { AbstractControl, FormArray, FormBuilder, FormGroup, ReactiveFormsModule, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { HelperServiceService } from '../../../../utils/helper-service.service';
import { ToasterService } from '../../../../providers/toaster/toaster.service';
import { StudentApiServiceService } from '../../../../pages/personnel/student/student-api-service.service';
import { format } from 'date-fns';
import { alphaNamePattern, alphaNumTextPattern, emailPattern, phonePattern, pincodePattern } from '../../../../providers/regexPattern';
import { LoaderComponent } from "../../../../providers/loader.component";
import { finalize, Observable, switchMap } from 'rxjs';
import { Store } from '@ngrx/store';
import { PreventEscCloseDirective } from '../../../../utils/prevent-esc-close.directive';
import { CommonApiDataServiceService } from '../../../../pages/services/common-api-data-service.service';
import { CascadingSelectServiceService } from '../../../../pages/services/cascading-select-service.service';

@Component({
  selector: 'app-student-form-modal',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, LoaderComponent, PreventEscCloseDirective],
  templateUrl: './student-form-modal.component.html',
  styleUrl: './student-form-modal.component.css'
})
export class StudentFormModalComponent {
  @ViewChild('modal') modal!: ElementRef<HTMLDialogElement>;

  @Input() studentId: any = null;
  @Input() isOpen: boolean = false;
  @Output() closeModalemmit = new EventEmitter();

  profile_pic: any = null;
  currentTab = 'personal';
  tabs = [
    { name: 'Personal Details', value: 'personal', isLock: false },
    { name: 'Address Details', value: 'address', isLock: true },
    { name: 'Parent Details', value: 'parent', isLock: true },
  ]

  relationshipList = [
    { label: 'Father', value: 'father' },
    { label: 'Mother', value: 'mother' },
    { label: 'Brother', value: 'brother' },
    { label: 'Sister', value: 'sister' },
    { label: 'Husband', value: 'husband' },
    { label: 'Wife', value: 'wife' },
    { label: 'Son', value: 'son' },
    { label: 'Daughter', value: 'daughter' },
    { label: 'Uncle', value: 'uncle' },
    { label: 'Aunt', value: 'aunt' },
    { label: 'Grandfather', value: 'grandfather' },
    { label: 'Grandmother', value: 'grandmother' },
    { label: 'Cousin', value: 'cousin' },
    { label: 'Guardian', value: 'guardian' },
    { label: 'Other', value: 'other' }
  ];


  type: 'new' | 'edit' = 'new';

  studentDetails: any = {};

  studentPersonalDetailsForm!: FormGroup
  studentAddressDetailsForm!: FormGroup
  studentParentDetailsForm!: FormGroup

  isreqSending: boolean = false
  isFetching: boolean = true

  academicEntities$!: Observable<any>
  departments: any[] = [];
  classes: any[] = [];
  batches: any[] = [];
  academicYears$!: Observable<any>
  isAcademicEntitesRenderd: boolean = true
  readonly todayDate = format(new Date(), 'yyyy-MM-dd');


  constructor(private fb: FormBuilder, private helperService: HelperServiceService, private toasterService: ToasterService
    , private studentApiService: StudentApiServiceService, private store: Store, private cascadedService: CascadingSelectServiceService
  ) {
  }

  getControlError(
    control: AbstractControl | null,
    fieldLabel: string,
    customMessages: Partial<Record<'required' | 'pattern' | 'maxlength' | 'minlength' | 'invalidDob', string>> = {}
  ): string | null {
    if (!control || !control.errors) {
      return null;
    }

    if (control.hasError('required')) {
      return customMessages.required ?? `${fieldLabel} is required`;
    }

    if (control.hasError('pattern')) {
      return customMessages.pattern ?? `Only alphabets are allowed for ${fieldLabel.toLowerCase()} and Trim extra spaces`;
    }

    if (control.hasError('invalidDob')) {
      return customMessages.invalidDob ?? `Enter a valid past ${fieldLabel.toLowerCase()}`;
    }

    if (control.hasError('maxlength')) {
      const limit = control.getError('maxlength')?.requiredLength;
      return customMessages.maxlength ?? `${fieldLabel} cannot exceed ${limit} characters`;
    }

    if (control.hasError('minlength')) {
      const limit = control.getError('minlength')?.requiredLength;
      return customMessages.minlength ?? `${fieldLabel} must be at least ${limit} characters`;
    }

    return null;
  }

  private pastDateValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const value = control.value;
      if (!value) {
        return null;
      }

      const selectedDate = new Date(value);
      const today = new Date();
      selectedDate.setHours(0, 0, 0, 0);
      today.setHours(0, 0, 0, 0);
      return selectedDate >= today ? { invalidDob: true } : null;
    };
  }

  ngOnInit() {

    this.academicEntities$ = this.cascadedService.academicEntries$
    this.academicEntities$.subscribe((data: any) => this.departments = data.data)
    this.academicYears$ = this.cascadedService.academicYear$



    this.studentPersonalDetailsForm = this.fb.group({
      id: [null],
      first_name: ['', [
        Validators.required,
        Validators.minLength(2),
        Validators.maxLength(50),
        // alphabets only, no numbers/special chars (spaces allowed between words)
        Validators.pattern(/^[A-Za-z]+(?:\s[A-Za-z]+)*$/),
      ]],
      last_name: ['', [
        Validators.required,
        Validators.minLength(2),
        Validators.maxLength(50),
        // alphabets only, no numbers/special chars (spaces allowed between words)
        Validators.pattern(/^[A-Za-z]+(?:\s[A-Za-z]+)*$/),
      ]],
      email_id: ['', [
        Validators.required,
        // no spaces
        Validators.pattern(/^[^\s]+$/),
        Validators.maxLength(100),
        // standard email format
        Validators.pattern(/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/),
      ]],
      mobile: ['', [
        Validators.required,
        // exactly 10 digits
        Validators.pattern(/^\d{10}$/),
      ]],
      date_of_birth: ['', [Validators.required, this.pastDateValidator()]],
      profile_photo: [null],
      department_id: [''],
      class_id: [''],
      batch_id: [''],
      academic_year: [''],
      twitter_handle: ['', [Validators.pattern(/^https?:\/\/(www\.)?(twitter\.com|x\.com)\/[A-Za-z0-9_]{1,15}$/)]],
      instagram_handle: ['', [Validators.pattern(/^https?:\/\/(www\.)?instagram\.com\/[A-Za-z0-9_\.]{1,30}\/?$/)]],
      linkedin_handle: ['', [Validators.pattern(/^https?:\/\/(www\.)?linkedin\.com\/in\/[A-Za-z0-9_-]{3,100}\/?$/)]],
    })

    const addressAllowedPattern = /^[A-Za-z0-9\s,\.\-\/#]+$/;
    // Only alphabets and spaces are allowed for city/district/state
    const nameWithSpacesPattern = /^[A-Za-z]+(?:\s[A-Za-z]+)*$/;
    const pincode10Pattern = /^\d{6}$/;

    this.studentAddressDetailsForm = this.fb.group({
      sameAsCurrent: [false],
      addresses: this.fb.array([
        this.fb.group({
          address_type: ['current'],
          address: ['', [
            Validators.required,
            Validators.minLength(5),
            Validators.maxLength(255),
            // Allowed: alphabets, numbers, spaces and special chars: , . - / #
            Validators.pattern(addressAllowedPattern),
          ]],
          city: ['', [
            Validators.required,
            Validators.minLength(2),
            Validators.maxLength(50),
            Validators.pattern(nameWithSpacesPattern),
          ]],
          district: ['', [
            Validators.required,
            Validators.minLength(2),
            Validators.maxLength(50),
            Validators.pattern(nameWithSpacesPattern),
          ]],
          state: ['', [
            Validators.required,
            Validators.minLength(2),
            Validators.maxLength(50),
            Validators.pattern(nameWithSpacesPattern),
          ]],
          pincode: ['', [
            Validators.required,
            Validators.pattern(pincode10Pattern),
          ]],
          country: ['', [Validators.required, Validators.maxLength(255)]],
        }),
        this.fb.group({
          address_type: ['permanent'],
          address: ['', [
            Validators.required,
            Validators.minLength(5),
            Validators.maxLength(255),
            // Allowed: alphabets, numbers, spaces and special chars: , . - / #
            Validators.pattern(addressAllowedPattern),
          ]],
          city: ['', [
            Validators.required,
            Validators.minLength(2),
            Validators.maxLength(50),
            Validators.pattern(nameWithSpacesPattern),
          ]],
          district: ['', [
            Validators.required,
            Validators.minLength(2),
            Validators.maxLength(50),
            Validators.pattern(nameWithSpacesPattern),
          ]],
          state: ['', [
            Validators.required,
            Validators.minLength(2),
            Validators.maxLength(50),
            Validators.pattern(nameWithSpacesPattern),
          ]],
          pincode: ['', [
            Validators.required,
            Validators.pattern(pincode10Pattern),
          ]],
          country: ['', [Validators.required, Validators.maxLength(255)]],
        })
      ]),
    });

    this.studentAddressDetailsForm.get('sameAsCurrent')?.valueChanges.subscribe(checked => {
      this.onSameAsCurrentChange(checked);
    });

    const emailIdPattern = /^[^\s]+@[^\s]+\.[^\s]{2,}$/; // no spaces + domain + TLD
    const mobilePattern = /\d{9}$/; // Indian mobile: 10 digits, 

    this.studentParentDetailsForm = this.fb.group({
      parent_name: ['', [
        Validators.required,
        Validators.maxLength(50),
        Validators.minLength(2),
        Validators.pattern(/^[A-Za-z]+(?:\s[A-Za-z]+)*$/),
      ]],
      email_id: ['', [
       
        Validators.maxLength(100),
        Validators.pattern(emailIdPattern),
        Validators.pattern(/^[^\s]+$/), // no spaces
      ]],
      mobile: ['', [
        Validators.required,
        Validators.pattern(mobilePattern),
      ]],
      relation_with_student: ['', [
        Validators.required,
        Validators.minLength(2),
        Validators.maxLength(30),
        Validators.pattern(/^[A-Za-z]+(?:\s[A-Za-z]+)*$/),
      ]],
      occupational_name: ['', [
       
        Validators.minLength(2),
        Validators.maxLength(255),
        Validators.pattern(alphaNumTextPattern)
      ]],
      occupational_email: ['', [
        
        Validators.pattern(emailPattern),
        Validators.maxLength(100),
      ]],
      occupational_mobile: ['', [
        
        Validators.pattern(phonePattern),
        Validators.maxLength(10),
      ]]
    });

    if (this.type === 'new') {
      this.studentPersonalDetailsForm.get('class_id')?.disable();
      this.studentPersonalDetailsForm.get('batch_id')?.disable();
    }

  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['isOpen'] && changes['isOpen'].currentValue) {
      this.type = this.studentId ? 'edit' : 'new';
      if (this.studentId && this.type === 'edit') {
        this.getStudentData(this.studentId);
        this.tabs.forEach((tab) => {
          tab.isLock = false;
        })
      } else {
        this.isFetching = false
      }
      console.log(this.type, this.studentId);
      setTimeout(() => {
        this.modal?.nativeElement?.showModal();
      });
    }
  }




  get addressArray(): FormArray {
    return this.studentAddressDetailsForm.get('addresses') as FormArray;
  }

  get currentAddress(): FormGroup {
    return this.addressArray.at(0) as FormGroup;
  }

  get permanentAddress(): FormGroup {
    return this.addressArray.at(1) as FormGroup;
  }

  get isDataReady(): boolean {
    return !this.isFetching;
  }

  onSameAsCurrentChange(checked: boolean) {
    const current = this.currentAddress as FormGroup;
    const permanent = this.permanentAddress as FormGroup;

    if (checked) {
      permanent.patchValue({ ...current.value, address_type: 'permanent' });
      permanent.disable();
    } else {
      permanent.enable();
      permanent.reset({ address_type: 'permanent' });
    }
  }

  getStudentData(studentId: number) {
    if (!studentId) {
      this.toasterService.setToaster('error', 'Failed to Fatch Data', 'studentId Not Found');
      return;
    }
    this.studentApiService.getStudentById(studentId).subscribe({
      next: (res: any) => {
        if (res.status === 'success') {
          this.studentDetails = res.data;
          this.patchValue(this.studentDetails);
        }
      },
      error: (err) => {
        console.error('HTTP error:', err);
      }
    })
  }


  patchValue(data: any) {
    this.studentPersonalDetailsForm.patchValue({
      id: data?.id,
      first_name: data?.first_name,
      last_name: data?.last_name,
      email_id: data?.email_id,
      mobile: data?.mobile,
      date_of_birth: data?.date_of_birth ? format(new Date(data.date_of_birth), 'yyyy-MM-dd') : null,
      profile_photo: null,
      department_id: data?.admission?.department_id,
      class_id: data?.admission?.class_id,
      batch_id: data?.admission?.batch_id,
      academic_year: data?.admission?.academic_year,
      twitter_handle: data?.twitter_handle,
      linkedin_handle: data?.linkdin_handle,
      instagram_handle: data?.instagram_handle,
    });
    this.profile_pic = data?.profile_photo || data?.image || null;

    if (data?.address) {
      this.studentAddressDetailsForm.patchValue({
        sameAsCurrent: false,
        addresses: [
          { ...data?.address[0], address_type: 'current' },
          { ...data?.address[1], address_type: 'permanent' },
        ]
      });
    }

    if (data?.parent_details) {
      this.studentParentDetailsForm.patchValue({
        ...data?.parent_details,
        relation_with_student: data?.parent_details?.relation_with_student.toLowerCase()
      });
    }

    this.checkHasAcedmicEntityValueSet(this.studentPersonalDetailsForm.value);

    // if (this.studentPersonalDetailsForm.get('class_id')?.value) {
    //   this.setFields(null, 'department', this.studentPersonalDetailsForm.get('department_id')?.value);
    // } else {
    //   this.studentPersonalDetailsForm.get('class_id')?.disable();
    // }

    // if (this.studentPersonalDetailsForm.get('class_id')?.value) {
    //   this.setFields(null, 'class', this.studentPersonalDetailsForm.get('class_id')?.value);
    // } else {
    //   this.studentPersonalDetailsForm.get('batch_id')?.disable();
    // }

    this.isFetching = false;
  }

  // *************************

  async checkHasAcedmicEntityValueSet(data: any) {
    if (data.class_id) {
      await this.setFields(null, 'department', data.department_id);
      this.studentPersonalDetailsForm.patchValue({ class_id: data.class_id });
    } else {
      this.studentPersonalDetailsForm.get('class_id')?.disable();
    }

    if (data.batch_id) {
      await this.setFields(null, 'class', data.class_id);
      this.studentPersonalDetailsForm.patchValue({ batch_id: data.batch_id });
    } else {
      this.studentPersonalDetailsForm.get('batch_id')?.disable();
    }



  }


  // save student details
  saveStudentDetails() {
    if (this.type === 'new') {
      this.toasterService.setToaster('error', 'Invalid action', 'For new student creation, complete all tabs and save from Parent Details.');
      return;
    }

    if (this.studentPersonalDetailsForm.invalid) {
      this.studentPersonalDetailsForm.markAllAsTouched();
      this.toasterService.setToaster('error', 'Validation Error', 'Please fix highlighted fields in Personal Details.');
      return;
    }

    const rawDob = this.studentPersonalDetailsForm.get('date_of_birth')?.value;
    const normalizedDob = rawDob ? format(new Date(rawDob), 'yyyy-MM-dd') : '';

    if (this.isInvalidDob(normalizedDob)) {
      this.toasterService.setToaster('error', 'Validation Error', 'Date of birth must be a valid past date.');
      return;
    }
    const formData = new FormData();
    // normalize names: trim extra spaces and collapse multiple spaces
    const firstName = (this.studentPersonalDetailsForm.value.first_name ?? '')
      .toString()
      .replace(/\s+/g, ' ')
      .trim();
    const lastName = (this.studentPersonalDetailsForm.value.last_name ?? '')
      .toString()
      .replace(/\s+/g, ' ')
      .trim();

    formData.append('first_name', firstName);
    formData.append('last_name', lastName);
    formData.append('email_id', this.studentPersonalDetailsForm.value.email_id);
    formData.append('mobile', this.studentPersonalDetailsForm.value.mobile);
    formData.append('date_of_birth', normalizedDob);
    if (this.studentPersonalDetailsForm.value.profile_photo) {
      formData.append('profile_photo', this.studentPersonalDetailsForm.value.profile_photo);
    }
    formData.append('admission_detail[department_id]', this.studentPersonalDetailsForm.value.department_id ?? '');
    formData.append('admission_detail[class_id]', this.studentPersonalDetailsForm.value.class_id ?? '');
    formData.append('admission_detail[batch_id]', this.studentPersonalDetailsForm.value.batch_id ?? '');
    formData.append('admission_detail[academic_year]', this.studentPersonalDetailsForm.value.academic_year ?? '');
    formData.append('twitter_handle', this.studentPersonalDetailsForm.value.twitter_handle);
    formData.append('instagram_handle', this.studentPersonalDetailsForm.value.instagram_handle);
    formData.append('linkdin_handle', this.studentPersonalDetailsForm.value.linkedin_handle);


    this.isreqSending = true;

    if (this.type === 'edit' && this.studentId) {
      this.studentApiService.updateStudentProfileDetails(formData, this.studentId).pipe(
        finalize(() => {
          this.isreqSending = false;
        })).subscribe({
          next: (res: any) => {
            if (res.status === 'success') {
              this.toasterService.setToaster('success', 'Success', res.message ?? 'Student updated successfully');
            } else {
              this.toasterService.setToaster('error', 'Failed', res.message ?? 'Something went wrong');
            }
          },
          error: (err) => {
            this.toasterService.setToaster('error', 'Failed', this.readHttpError(err, 'Unable to update student details'));
          }
        }
        )
    }
  }

  private validateFormsForNewCreation(): boolean {
    let ok = true;

    if (this.studentPersonalDetailsForm.invalid) {
      this.studentPersonalDetailsForm.markAllAsTouched();
      ok = false;
      this.toasterService.setToaster('error', 'Validation Error', 'Please fix highlighted fields in Personal Details.');
    }

    if (this.studentAddressDetailsForm.invalid) {
      this.studentAddressDetailsForm.markAllAsTouched();
      ok = false;
      this.toasterService.setToaster('error', 'Validation Error', 'Please fix highlighted fields in Address Details.');
    }

    if (this.studentParentDetailsForm.invalid) {
      this.studentParentDetailsForm.markAllAsTouched();
      ok = false;
      this.toasterService.setToaster('error', 'Validation Error', 'Please fix highlighted fields in Parent Details.');
    }

    return ok;
  }

  private markAllControlsTouched(formGroup: FormGroup) {
    Object.keys(formGroup.controls).forEach(key => {
      const control = formGroup.get(key);
      if (control) {
        if (control instanceof FormGroup) {
          this.markAllControlsTouched(control);
        } else if (control instanceof FormArray) {
          control.controls.forEach(c => {
            if (c instanceof FormGroup) {
              this.markAllControlsTouched(c);
            } else {
              c.markAsTouched();
            }
          });
        } else {
          control.markAsTouched();
        }
      }
    });
  }

  private buildNewStudentFormData(): FormData {
    const rawDob = this.studentPersonalDetailsForm.get('date_of_birth')?.value;
    const normalizedDob = rawDob ? format(new Date(rawDob), 'yyyy-MM-dd') : '';

    const formData = new FormData();
    formData.append('first_name', this.studentPersonalDetailsForm.value.first_name);
    formData.append('last_name', this.studentPersonalDetailsForm.value.last_name);
    formData.append('email_id', this.studentPersonalDetailsForm.value.email_id);
    formData.append('mobile', this.studentPersonalDetailsForm.value.mobile);
    formData.append('date_of_birth', normalizedDob);
    formData.append('role', 'student');

    if (this.studentPersonalDetailsForm.value.profile_photo) {
      formData.append('profile_photo', this.studentPersonalDetailsForm.value.profile_photo);
    }

    formData.append('admission_detail[department_id]', this.studentPersonalDetailsForm.value.department_id ?? '');
    formData.append('admission_detail[class_id]', this.studentPersonalDetailsForm.value.class_id ?? '');
    formData.append('admission_detail[batch_id]', this.studentPersonalDetailsForm.value.batch_id ?? '');
    formData.append('admission_detail[academic_year]', this.studentPersonalDetailsForm.value.academic_year ?? '');
    formData.append('twitter_handle', this.studentPersonalDetailsForm.value.twitter_handle);
    formData.append('instagram_handle', this.studentPersonalDetailsForm.value.instagram_handle);
    formData.append('linkdin_handle', this.studentPersonalDetailsForm.value.linkedin_handle);

    return formData;
  }

  private buildAddressData(): any[] {
    const addressData = this.studentAddressDetailsForm.getRawValue().addresses.map((address: any) => ({
      ...address,
      pincode: address.pincode.toString(),
    }));
    return addressData;
  }

  private validateDobOrToast(): boolean {
    const rawDob = this.studentPersonalDetailsForm.get('date_of_birth')?.value;
    const normalizedDob = rawDob ? format(new Date(rawDob), 'yyyy-MM-dd') : '';

    if (this.isInvalidDob(normalizedDob)) {
      this.toasterService.setToaster('error', 'Validation Error', 'Date of birth must be a valid past date.');
      return false;
    }
    return true;
  }

  private savePersonalTabForNewCreation(): Observable<any> {
    this.markAllControlsTouched(this.studentPersonalDetailsForm);
    if (this.studentPersonalDetailsForm.invalid) {
      this.toasterService.setToaster('error', 'Validation Error', 'Please fix highlighted fields in Personal Details.');
      throw new Error('Personal form invalid');
    }

    if (!this.validateDobOrToast()) {
      throw new Error('Invalid DOB');
    }

    const formData = this.buildNewStudentFormData();

    this.isreqSending = true;
    return this.studentApiService.createStudentProfileDetails(formData).pipe(
      switchMap((res: any) => {
        if (res.status !== 'success' || !res.data?.id) {
          throw new Error(res.message || 'Failed to create student');
        }
        this.studentId = res.data.id;
        this.type = 'edit';
        return new Observable<any>(subscriber => {
          subscriber.next(res);
          subscriber.complete();
        });
      }),
      finalize(() => {
        this.isreqSending = false;
      })
    );
  }

  private saveAddressTabForNewCreation(): Observable<any> {
    this.markAllControlsTouched(this.studentAddressDetailsForm);
    if (this.studentAddressDetailsForm.invalid) {
      this.toasterService.setToaster('error', 'Validation Error', 'Please fix highlighted fields in Address Details.');
      throw new Error('Address form invalid');
    }

    if (!this.studentId) {
      this.toasterService.setToaster('error', 'Missing Student ID', 'Cannot save address without student ID');
      throw new Error('Missing studentId');
    }

    const addressData = this.buildAddressData();

    this.isreqSending = true;
    return this.studentApiService.setStudentAddressDetails(addressData, this.studentId).pipe(
      finalize(() => {
        this.isreqSending = false;
      })
    );
  }

  private saveParentTabForNewCreation(): Observable<any> {
    this.markAllControlsTouched(this.studentParentDetailsForm);
    if (this.studentParentDetailsForm.invalid) {
      this.studentParentDetailsForm.markAllAsTouched();
      this.toasterService.setToaster('error', 'Validation Error', 'Please fix highlighted fields in Parent Details.');
      throw new Error('Parent form invalid');
    }

    if (!this.studentId) {
      this.toasterService.setToaster('error', 'Missing Student ID', 'Cannot save parent details without student ID');
      throw new Error('Missing studentId');
    }

    this.isreqSending = true;
    return this.studentApiService.setStudentParentDetails(this.studentParentDetailsForm.value, this.studentId).pipe(
      finalize(() => {
        this.isreqSending = false;
      })
    );
  }

  goToAddressTab() {
    if (this.type === 'new') {
      this.savePersonalTabForNewCreation().subscribe({
        next: (res: any) => {
          // unlock + navigate only after successful student creation
          this.tabs.find(t => t.value === 'address')!.isLock = false;
          this.currentTab = 'address';
        },
        error: (err: any) => {
          // Handle backend validation error: mobile does not match existing email
          const msg = this.readHttpError(err, 'Unable to create student');
          const mobileErr = err?.error?.data?.mobile ?? err?.error?.message ?? err?.message;
          const mobileCtrl = this.studentPersonalDetailsForm.get('mobile');

          if (mobileCtrl && typeof mobileErr === 'string') {
            if (mobileErr.toLowerCase().includes('mobile') && mobileErr.toLowerCase().includes('email')) {
              mobileCtrl.setErrors({ ...(mobileCtrl.errors ?? {}), mobileEmailMismatch: mobileErr });
              mobileCtrl.markAsTouched();
              this.toasterService.setToaster('error', 'Validation Error', mobileErr);
              return;
            }
          }

          this.toasterService.setToaster('error', 'Failed', msg);
        }
      });
      return;
    }

    if (this.studentPersonalDetailsForm.invalid) {
      this.studentPersonalDetailsForm.markAllAsTouched();
      this.toasterService.setToaster('error', 'Validation Error', 'Please fix highlighted fields in Personal Details.');
      return;
    }

    const rawDob = this.studentPersonalDetailsForm.get('date_of_birth')?.value;
    const normalizedDob = rawDob ? format(new Date(rawDob), 'yyyy-MM-dd') : '';
    if (this.isInvalidDob(normalizedDob)) {
      this.toasterService.setToaster('error', 'Validation Error', 'Date of birth must be a valid past date.');
      return;
    }

    const formData = new FormData();
    formData.append('first_name', this.studentPersonalDetailsForm.value.first_name);
    formData.append('last_name', this.studentPersonalDetailsForm.value.last_name);
    formData.append('email_id', this.studentPersonalDetailsForm.value.email_id);
    formData.append('mobile', this.studentPersonalDetailsForm.value.mobile);
    formData.append('date_of_birth', normalizedDob);
    if (this.studentPersonalDetailsForm.value.profile_photo) {
      formData.append('profile_photo', this.studentPersonalDetailsForm.value.profile_photo);
    }
    formData.append('role', 'Student');
    formData.append('admission_detail[department_id]', this.studentPersonalDetailsForm.value.department_id ?? '');
    formData.append('admission_detail[class_id]', this.studentPersonalDetailsForm.value.class_id ?? '');
    formData.append('admission_detail[batch_id]', this.studentPersonalDetailsForm.value.batch_id ?? '');
    formData.append('admission_detail[academic_year]', this.studentPersonalDetailsForm.value.academic_year ?? '');
    formData.append('twitter_handle', this.studentPersonalDetailsForm.value.twitter_handle);
    formData.append('instagram_handle', this.studentPersonalDetailsForm.value.instagram_handle);
    formData.append('linkdin_handle', this.studentPersonalDetailsForm.value.linkedin_handle);

    if (!this.studentId) {
      this.toasterService.setToaster('error', 'Missing Student ID', 'Cannot update personal details without student ID');
      return;
    }

    this.isreqSending = true;
    this.studentApiService.updateStudentProfileDetails(formData, this.studentId).pipe(
      finalize(() => {
        this.isreqSending = false;
      })
    ).subscribe({
      next: (res: any) => {
        if (res?.status === 'success') {
          this.tabs.find(t => t.value === 'address')!.isLock = false;
          this.currentTab = 'address';
          this.toasterService.setToaster('success', 'Success', res.message ?? 'Personal details updated successfully');
        } else {
          this.toasterService.setToaster('error', 'Failed', res?.message ?? 'Unable to update personal details');
        }
      },
      error: (err: any) => {
        this.toasterService.setToaster('error', 'Failed', this.readHttpError(err, 'Unable to update personal details'));
      }
    });
  }


  goToParentTab() {
    if (this.type === 'new') {
      this.saveAddressTabForNewCreation().subscribe({
        next: (res: any) => {
          if (res?.status === 'success') {
            this.tabs.find(t => t.value === 'parent')!.isLock = false;
            this.currentTab = 'parent';
          } else {
            this.toasterService.setToaster('error', 'Failed', res?.message ?? 'Unable to save address details');
          }
        },
        error: (err: any) => {
          this.toasterService.setToaster('error', 'Failed', this.readHttpError(err, 'Unable to save address details'));
        }
      });
      return;
    }

    this.markAllControlsTouched(this.studentAddressDetailsForm);
    if (this.studentAddressDetailsForm.invalid) {
      this.studentAddressDetailsForm.markAllAsTouched();
      this.toasterService.setToaster('error', 'Validation Error', 'Please fix highlighted fields in Address Details.');
      return;
    }

    if (!this.studentId) {
      this.toasterService.setToaster('error', 'Missing Student ID', 'Cannot update address without student ID');
      return;
    }

    const addressData = this.buildAddressData();

    this.isreqSending = true;
    this.studentApiService.updateStudentAddressDetails(addressData, this.studentId).pipe(
      finalize(() => {
        this.isreqSending = false;
      })
    ).subscribe({
      next: (res: any) => {
        if (res?.status === 'success') {
          this.tabs.find(t => t.value === 'parent')!.isLock = false;
          this.currentTab = 'parent';
          this.toasterService.setToaster('success', 'Success', res.message ?? 'Address details updated successfully');
        } else {
          this.toasterService.setToaster('error', 'Failed', res?.message ?? 'Unable to update address details');
        }
      },
      error: (err: any) => {
        this.toasterService.setToaster('error', 'Failed', this.readHttpError(err, 'Unable to update address details'));
      }
    });
  }


  saveNewStudentAllDetails() {
    if (!this.validateFormsForNewCreation()) {
      return;
    }

    const rawDob = this.studentPersonalDetailsForm.get('date_of_birth')?.value;
    const normalizedDob = rawDob ? format(new Date(rawDob), 'yyyy-MM-dd') : '';

    if (this.isInvalidDob(normalizedDob)) {
      this.toasterService.setToaster('error', 'Validation Error', 'Date of birth must be a valid past date.');
      return;
    }

    const formData = new FormData();
    // normalize names: trim extra spaces and collapse multiple spaces
    const firstName = (this.studentPersonalDetailsForm.value.first_name ?? '')
      .toString()
      .replace(/\s+/g, ' ')
      .trim();
    const lastName = (this.studentPersonalDetailsForm.value.last_name ?? '')
      .toString()
      .replace(/\s+/g, ' ')
      .trim();

    formData.append('first_name', firstName);
    formData.append('last_name', lastName);
    formData.append('email_id', this.studentPersonalDetailsForm.value.email_id);
    formData.append('mobile', this.studentPersonalDetailsForm.value.mobile);
    formData.append('date_of_birth', normalizedDob);
    formData.append('role', "student");
    if (this.studentPersonalDetailsForm.value.profile_photo) {
      formData.append('profile_photo', this.studentPersonalDetailsForm.value.profile_photo);
    }
    formData.append('admission_detail[department_id]', this.studentPersonalDetailsForm.value.department_id ?? '');
    formData.append('admission_detail[class_id]', this.studentPersonalDetailsForm.value.class_id ?? '');
    formData.append('admission_detail[batch_id]', this.studentPersonalDetailsForm.value.batch_id ?? '');
    formData.append('admission_detail[academic_year]', this.studentPersonalDetailsForm.value.academic_year ?? '');
    formData.append('twitter_handle', this.studentPersonalDetailsForm.value.twitter_handle);
    formData.append('instagram_handle', this.studentPersonalDetailsForm.value.instagram_handle);
    formData.append('linkdin_handle', this.studentPersonalDetailsForm.value.linkedin_handle);

    const addressData = this.studentAddressDetailsForm.getRawValue().addresses.map((address: any) => ({ ...address, pincode: address.pincode.toString() }));
    const parentData = this.studentParentDetailsForm.value;

    this.isreqSending = true;

    this.studentApiService.createStudentProfileDetails(formData).pipe(
      switchMap((res: any) => {
        if (res.status !== 'success' || !res.data?.id) {
          throw new Error(res.message || 'Failed to create student');
        }
        this.studentId = res.data.id;
        this.type = 'edit';
        return this.studentApiService.setStudentAddressDetails(addressData, this.studentId);
      }),
      switchMap((res: any) => {
        if (res.status !== 'success') {
          throw new Error(res.message || 'Failed to save address details');
        }
        return this.studentApiService.setStudentParentDetails(parentData, this.studentId);
      }),
      finalize(() => {
        this.isreqSending = false;
      })
    ).subscribe({
      next: (res: any) => {
        if (res.status === 'success') {
          this.toasterService.setToaster('success', 'Success', 'Student created successfully with all details.');
          this.tabs.forEach(tab => tab.isLock = false);
          this.currentTab = 'personal';
          this.emitData('create');
          this.closeModal();
        } else {
          this.toasterService.setToaster('error', 'Failed', res.message ?? 'Something went wrong while saving parent details');
        }
      },
      error: (err) => {
        this.toasterService.setToaster('error', 'Failed', this.readHttpError(err, 'Unable to complete student creation'));
      }
    });
  }

  saveStudentLastTab() {
    if (this.isreqSending) return;

    if (this.type === 'new') {
      // Parent tab (final step) only: save parent details using already-created studentId
      this.saveParentTabForNewCreation().subscribe({
        next: (res: any) => {
          if (res?.status === 'success') {
            this.toasterService.setToaster('success', 'Success', res.message ?? 'Student created successfully with all details.');
            this.tabs.forEach(tab => tab.isLock = false);
            this.currentTab = 'personal';
            this.emitData('create');
            this.closeModal();
          } else {
            this.toasterService.setToaster('error', 'Failed', res?.message ?? 'Unable to save parent details');
          }
        },
        error: (err: any) => {
          this.toasterService.setToaster('error', 'Failed', this.readHttpError(err, 'Unable to save parent details'));
        }
      });
      return;
    }

    this.saveEditStudentAllDetails();
  }


  saveEditStudentAllDetails() {
    if (!this.validateFormsForNewCreation()) {
      return;
    }

    if (!this.studentId) {
      this.toasterService.setToaster('error', 'Missing Student ID', 'Cannot update student without ID');
      return;
    }

    const rawDob = this.studentPersonalDetailsForm.get('date_of_birth')?.value;
    const normalizedDob = rawDob ? format(new Date(rawDob), 'yyyy-MM-dd') : '';

    if (this.isInvalidDob(normalizedDob)) {
      this.toasterService.setToaster('error', 'Validation Error', 'Date of birth must be a valid past date.');
      return;
    }

    const formData = new FormData();
    // normalize names: trim extra spaces and collapse multiple spaces
    const firstName = (this.studentPersonalDetailsForm.value.first_name ?? '')
      .toString()
      .replace(/\s+/g, ' ')
      .trim();
    const lastName = (this.studentPersonalDetailsForm.value.last_name ?? '')
      .toString()
      .replace(/\s+/g, ' ')
      .trim();

    formData.append('first_name', firstName);
    formData.append('last_name', lastName);
    formData.append('email_id', this.studentPersonalDetailsForm.value.email_id);
    formData.append('mobile', this.studentPersonalDetailsForm.value.mobile);
    formData.append('role', "Student");
    formData.append('date_of_birth', normalizedDob);
    if (this.studentPersonalDetailsForm.value.profile_photo) {
      formData.append('profile_photo', this.studentPersonalDetailsForm.value.profile_photo);
    }
    formData.append('admission_detail[department_id]', this.studentPersonalDetailsForm.value.department_id ?? '');
    formData.append('admission_detail[class_id]', this.studentPersonalDetailsForm.value.class_id ?? '');
    formData.append('admission_detail[batch_id]', this.studentPersonalDetailsForm.value.batch_id ?? '');
    formData.append('admission_detail[academic_year]', this.studentPersonalDetailsForm.value.academic_year ?? '');
    formData.append('twitter_handle', this.studentPersonalDetailsForm.value.twitter_handle);
    formData.append('instagram_handle', this.studentPersonalDetailsForm.value.instagram_handle);
    formData.append('linkdin_handle', this.studentPersonalDetailsForm.value.linkedin_handle);

    const addressData = this.studentAddressDetailsForm.getRawValue().addresses.map((address: any) => ({ ...address, pincode: address.pincode.toString() }));
    const parentData = this.studentParentDetailsForm.value;

    this.isreqSending = true;

    this.studentApiService.updateStudentProfileDetails(formData, this.studentId).pipe(
      switchMap((res: any) => {
        if (res.status !== 'success') {
          throw new Error(res.message || 'Failed to update student');
        }
        return this.studentApiService.updateStudentAddressDetails(addressData, this.studentId);
      }),
      switchMap((res: any) => {
        if (res.status !== 'success') {
          throw new Error(res.message || 'Failed to save address details');
        }
        return this.studentApiService.updateStudentParentDetails(parentData, this.studentId);
      }),
      finalize(() => {
        this.isreqSending = false;
      })
    ).subscribe({
      next: (res: any) => {
        if (res.status === 'success') {
          this.toasterService.setToaster('success', 'Success', 'Student updated successfully with all details.');
          this.emitData('update');
          this.closeModal();
        } else {
          this.toasterService.setToaster('error', 'Failed', res.message ?? 'Something went wrong while saving parent details');
        }
      },
      error: (err) => {
        this.toasterService.setToaster('error', 'Failed', this.readHttpError(err, 'Unable to complete student update'));
      }
    });
  }

  saveStudentAddressDetails() {
    if (this.studentAddressDetailsForm.invalid) {
      this.studentAddressDetailsForm.markAllAsTouched();
      this.toasterService.setToaster('error', 'Validation Error', 'Please fix highlighted fields in Address Details.');
      return;
    }

    if (!this.studentId) {
      this.toasterService.setToaster('error', 'Missing Student ID', 'Cannot save address without student ID');
      return;
    }

    const addressValue = this.studentAddressDetailsForm.getRawValue();
    let data = addressValue.addresses;
    data = data.map((address: any) => ({ ...address, pincode: address.pincode.toString() }));
    this.isreqSending = true;

    const req$ = this.type === 'edit'
      ? this.studentApiService.updateStudentAddressDetails(data, this.studentId)
      : this.studentApiService.setStudentAddressDetails(data, this.studentId);

    req$.pipe(
      finalize(() => {
        this.isreqSending = false;
      }))
      .subscribe({
        next: (res: any) => {
          if (res.status === 'success') {
            this.toasterService.setToaster('success', 'Success', res.message ?? 'Address details updated successfully');
          }
        },
        error: (err) => {
          this.toasterService.setToaster('error', 'Failed', this.readHttpError(err, 'Unable to save address details'));
        }
      });

  }


  saveStudentParentDetails() {
    if (this.studentParentDetailsForm.invalid) {
      this.studentParentDetailsForm.markAllAsTouched();
      this.toasterService.setToaster('error', 'Validation Error', 'Please fix highlighted fields in Parent Details.');
      return;
    }

    if (!this.studentId) {
      this.toasterService.setToaster('error', 'Missing Student ID', 'Cannot save parent without student ID');
      return;
    }

    this.isreqSending = true;

    const req$ = this.type === 'edit'
      ? this.studentApiService.updateStudentParentDetails(this.studentParentDetailsForm.value, this.studentId)
      : this.studentApiService.setStudentParentDetails(this.studentParentDetailsForm.value, this.studentId);

    req$.pipe(
      finalize(() => {
        this.isreqSending = false;
      }))
      .subscribe({
        next: (res: any) => {
          if (res.status === 'success') {
            this.toasterService.setToaster('success', 'Success', res.message ?? 'Parent details updated successfully');
          }
        },
        error: (err) => {
          this.toasterService.setToaster('error', 'Failed', this.readHttpError(err, 'Unable to save parent details'));
        }
      });
  }


  // ************************* 
  setProfilePic(event: any) {
    const { image, imageUrl } = this.helperService.getImageFileAndUrl(event);
    if (image) {
      this.studentPersonalDetailsForm.patchValue({
        profile_photo: image
      })
    }
    this.profile_pic = imageUrl;
  }

  closeModal() {
    this.modal?.nativeElement?.close();
    this.profile_pic = null;
    this.studentId = null;
    this.isOpen = false;
    this.currentTab = 'personal';
    this.tabs.forEach((tab) => {
      tab.isLock = true;
    })
    this.tabs[0].isLock = false;
    this.studentPersonalDetailsForm.reset();
    this.studentAddressDetailsForm.reset();
    this.studentParentDetailsForm.reset();
    this.classes = [];
    this.batches = [];
    this.emitData('close');
  }

  emitData(action: string) {
    console.log('action', action);
    this.closeModalemmit.emit(action);

  }



  setFields(event: Event | null, type: 'department' | 'class', id?: any) {
    const target = event?.target as HTMLSelectElement;
    const value = target?.value ?? id;

    console.log(this.studentPersonalDetailsForm.value);
    console.log('value', value);


    if (!value || value === 'null') {
      if (type === 'department') {
        this.studentPersonalDetailsForm.patchValue({ class_id: null, batch_id: null });
        this.studentPersonalDetailsForm.get('class_id')?.disable();
        this.studentPersonalDetailsForm.get('batch_id')?.disable();
        this.classes = [];
        this.batches = [];
      } else if (type === 'class') {
        this.studentPersonalDetailsForm.patchValue({ batch_id: null });
        this.studentPersonalDetailsForm.get('batch_id')?.disable();
        this.batches = [];
      }
      return;
    }

    const numericValue = +value;

    if (type === 'department') {
      const matched = this.departments.find(d => d.id === numericValue);
      this.classes = matched?.classes || [];
      this.batches = [];

      this.studentPersonalDetailsForm.patchValue({ class_id: null, batch_id: null });
      this.studentPersonalDetailsForm.get('class_id')?.enable();
      this.studentPersonalDetailsForm.get('batch_id')?.disable();
    }

    if (type === 'class') {
      const matched = this.classes.find(c => c.id === numericValue);
      this.batches = matched?.batches || [];

      this.studentPersonalDetailsForm.patchValue({ batch_id: null });
      this.studentPersonalDetailsForm.get('batch_id')?.enable();
    }
  }

  isInvalidDob(value: string): boolean {
    if (!value) {
      return true;
    }
    const selectedDate = new Date(value);
    const today = new Date();
    selectedDate.setHours(0, 0, 0, 0);
    today.setHours(0, 0, 0, 0);
    return selectedDate >= today;
  }

  private toApiDob(value: string): string {
    // Convert yyyy-MM-dd to an ISO string based on local midnight.
    const [yearStr, monthStr, dayStr] = value.split('-');
    const year = Number(yearStr);
    const month = Number(monthStr);
    const day = Number(dayStr);
    const localMidnight = new Date(year, month - 1, day);
    return localMidnight.toISOString();
  }

  readHttpError(err: any, fallback: string): string {
    return err?.error?.message || err?.error?.error || err?.message || fallback;
  }


}
